import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { getSalatOrder, cancelSalatOrder } from "../../api";
import ConvertToArabicNumbers from "../../utils/convert_numbers";
import { StatusBadge } from "./salatOrders";

const SingleSalatOrder = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const { orderId } = useParams();
  const queryClient = useQueryClient();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const { data: order, isLoading, error } = useQuery({
    queryKey: ["salatOrder", orderId],
    queryFn: () => getSalatOrder(orderId),
    enabled: !!orderId,
  });

  const formatMoney = (value) => {
    const formatted = value.toFixed(2);
    return isRtl ? ConvertToArabicNumbers(formatted) : formatted;
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await cancelSalatOrder(orderId);
      toast.success(t("salatMahsool.orders.cancelSuccess"));
      queryClient.invalidateQueries(["salatOrder", orderId]);
      queryClient.invalidateQueries(["salatOrders"]);
    } catch (e) {
      toast.error(t("salatMahsool.orders.cancelFailed"));
    } finally {
      setCancelling(false);
      setShowCancelModal(false);
    }
  };

  if (isLoading) {
    return <div className="text-center mt-10 text-gray-600">{t("common.loading")}</div>;
  }

  if (error || !order) {
    return <div className="text-center mt-10 text-red-500">{t("salatMahsool.orders.notFound")}</div>;
  }

  return (
    <div dir={isRtl ? "rtl" : "ltr"} className="max-w-4xl mx-auto px-6 py-10">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-primary">
          {t("salatMahsool.confirmation.orderNumber")} #{order.id}
        </h1>
        <StatusBadge status={order.status} />
      </div>

      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="font-bold text-lg mb-4">{t("salatMahsool.checkout.orderSummary")}</h2>
        <div className="flex flex-col gap-2 mb-4">
          {order.items?.map((line) => (
            <div key={line.id} className="flex justify-between text-sm border-b pb-2">
              <span>
                {line.product_name} × {line.quantity}
              </span>
              <span>
                {formatMoney(parseFloat(line.subtotal))} {t("common.currency")}
              </span>
            </div>
          ))}
        </div>
        <div className="flex justify-between font-bold text-primary text-lg mb-6">
          <span>{t("salatMahsool.confirmation.total")}</span>
          <span>
            {formatMoney(parseFloat(order.total_amount))} {t("common.currency")}
          </span>
        </div>

        {(order.address || order.notes) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {order.address && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-500 mb-1">{t("salatMahsool.checkout.address")}</p>
                <p className="font-semibold">{order.address}</p>
              </div>
            )}
            {order.notes && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-500 mb-1">{t("salatMahsool.checkout.notes")}</p>
                <p className="font-semibold">{order.notes}</p>
              </div>
            )}
          </div>
        )}

        {order.status === "Pending" && (
          <button
            onClick={() => setShowCancelModal(true)}
            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
          >
            {t("salatMahsool.orders.cancel")}
          </button>
        )}
      </div>

      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-xl w-96">
            <h2 className="text-xl font-bold mb-4 text-red-600">{t("salatMahsool.orders.confirmCancel")}</h2>
            <div className="flex justify-end gap-4 mt-4">
              <button
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                onClick={() => setShowCancelModal(false)}
                disabled={cancelling}
              >
                {t("common.cancel")}
              </button>
              <button
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
                onClick={handleCancel}
                disabled={cancelling}
              >
                {t("common.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SingleSalatOrder;
